// Разделение лендинга и кабинета по домену / пути (см. isAppDomain).
import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ConfigProvider, Spin, message } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import { antdTheme } from './config/theme';
import { Provider, useDispatch } from 'react-redux';
import { store } from './store';
import { checkAuth, skipAuthCheck, clearSessionLocal } from './store/authSlice';
import { SESSION_REVALIDATE_MIN_MS } from './utils/sessionGuard';
// ✅ ПЕРЕНЕСЕНО В МОДУЛИ:
// - BillingContext → markbaseCORE/modules/wallet/frontend/src/contexts/BillingContext.jsx
// - TariffContext → markbaseCORE/modules/tariffs/frontend/src/contexts/TariffContext.jsx
// Локальные адаптеры: billing-виджеты App (не страница /tariffs — она редиректит на UAM).
import { BillingProvider } from './contexts/BillingContext';
import { TariffProvider } from './contexts/TariffContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { ToastProvider } from './contexts/ToastContext';
import axios from './utils/axios';
import { isAppDomain } from './utils/isAppDomain';
import { resolveMarkbaseProductionAppHostname, isPublicMarketingContentPath, resolveMarketingAppRedirectHref } from './utils/markbaseSpaHosts';
import { devLogApiError } from './utils/devLogApiError';
import {
  UAM_URL,
  SCHEDULER_DASHBOARD_URL,
  BACKUP_DASHBOARD_URL,
  WALLET_PAGE_URL,
  BILLING_PAGE_URL,
  BILLING_ADMIN_PAGE_URL,
  resolveDocsPortalUrl,
  shouldRedirectMarketingDocsToPortal,
} from './utils/externalLinks';
// Auth/error и базовый shell — синхронно для первого кадра.
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import EmailVerification from './pages/Auth/EmailVerification';
import AuthCheckPage from './pages/Auth/AuthCheckPage';
import AuthCallbackPage from './pages/Auth/AuthCallbackPage';
import ForbiddenPage from './pages/Error/ForbiddenPage';
import NotFoundPage from './pages/Error/NotFoundPage';
import AccountSuspendedPage from './pages/Error/AccountSuspendedPage';
import InternalServerErrorPage from './pages/Error/InternalServerErrorPage';
import UnauthorizedPage from './pages/Error/UnauthorizedPage';
import MaintenancePage from './pages/Error/MaintenancePage';
import TooManyRequestsPage from './pages/Error/TooManyRequestsPage';

import ProtectedRoute from './components/Auth/ProtectedRoute';
import Layout from './components/Layout/Layout';
import DeployUpdateScreen from './components/Error/DeployUpdateScreen';
import { PERMISSIONS } from 'utils/constants';

// Code-split страницы кабинета и модулей (F-MBRU-MAIN_APP-UI-13); auth/error/лендинг — синхронно.
import {
  Dashboard,
  ProductsPage,
  SuppliersPage,
  WarehousesPage,
  SettingsPage,
  NotificationsPage,
  NotificationsConnectionPage,
  MenuGuidePage,
  ProductImportWizard,
  ProductGroupsPage,
  MarkingPage,
  ExpirySettingsPage,
  OrderDetails,
  WarehouseManager,
  UlrOperationsPage,
  AnalyticsPage,
  AnalyticsSettingsPage,
  AnalyticsConnectionPage,
  PriceHubPage,
  CurrencySettingsPage,
  CostDetailsPage,
  FinanceOverviewPage,
  ApiKeysHubPage,
  DictionariesPage,
  BrandDiscountsPage,
  SupplierMapping,
  ImagesManagementPage,
  FileManagementPage,
  AutomationsPage,
  CatalogExportsPage,
  CatalogExportWizard,
  CatalogExportEditor,
  ShopIntegrationAuthorizePage,
  SEOPage,
  LandingConnectionPage,
  PlatformMetaConnectionPage,
  CompaniesPage,
  CompanyUsersPageHost,
  BillingManagementPage,
  RegistryAdminHubPage,
  NotificationsAdminPage,
  KassaPOSPage,
  KassaAdminPage,
  KassaConnectionPage,
  KassaReportsPage,
  SelfCheckoutPage,
  LoyaltyCardPage,
  ElectronicPOAPage,
  CompliancePage,
  ComplianceConnectionPage,
  DocumentsPage,
  DocumentDetailPage,
  DocumentsIncomingPage,
  DocumentsSettingsPage,
  DocumentsConnectionPage,
  StockPiecesPage,
  PricingTemplatesAdminPage,
  PricingRulesAdminPage,
  ResourceStylesPage,
  ApiRateLimitsPage,
  ContainerResourcesAdminPage,
  BackgroundJobsAdminPage,
  PluginHubAdminPage,
  PluginHubConnectionPage,
  LoyaltySettingsAdminPage,
  NavigationNavAdminPage,
  DatabaseConsolePage,
  LogCenterAdminPage,
  LogCenterConnectionPage,
  ConcurensMapAdminPage,
  ConcurensSettingsAdminPage,
  CompanionAdminPage,
  BackupConnectionPage,
  DockerAgentConnectionPage,
  ProcessCenterAdminPage,
  ProcessCenterConnectionPage,
  MonitoringConnectionPage,
  ControlPlaneAdminPage,
  ControlPlaneConnectionPage,
  AiWaygptSettingsHubPage,
  NeuroCompanionOpenPage,
  NeuroActionHistoryPage,
  SecurityDashboardPage,
  ChatPage,
  ChatsPage,
  ChatsConnectionPage,
  ConnectedCompaniesPage,
  StyleShowcasePage,
  OrdersModulePage,
  DeliveryModulePage,
  LogisticsModulePage,
  IntegrationsAdminModulePage,
  CompanyWorkspacePage,
  CrmModulePage,
  HrmModulePage,
  FilesModulePage,
  FilesConnectionPage,
  KassaModulePage,
  SkladWmsModulePage,
  LegalModulePage,
  LegalConnectionPage,
  ServicesModulePage,
  CompanyServicesBookingHubPage,
  SupportModulePage,
  SupportConnectionPage,
  SearchModulePage,
  SearchConnectionPage,
  BonusModulePage,
  BonusConnectionPage,
  ShopModulePage,
  FeedModulePage,
  FeedConnectionPage,
  PowerModulePage,
  PowerConnectionPage,
  TorgPage,
  TorgConnectionPage,
  FinanceModulePage,
  FinanceConnectionPage,
  WidgetSystemModulePage,
  WidgetSystemConnectionPage,
  WaygenPage,
  WaygenConnectionPage,
  UsersConnectionPage,
  PayKonturConnectionPage,
  CommerceHubPage,
  OpsCenterPage,
  AiserverWaygptConnectionPage,
  WaygptKonturConnectionPage,
  MarketplaceKonturConnectionPage,
  OurCompanyPage,
  CompanyAnalysisPage,
  CompanyCreationWizard,
  JoinCompanyPage,
  WayGPTPage,
  MplazaPage,
  MplazaConnectionPage,
  MplazaAppConnectionPage,
  CoreSystemPage,
  ConstructorPage,
  CrmHrmPage,
  AnalyticsFinancePage,
  LogisticsDocsPage,
  ServicesMarketPage,
  SecurityAdminPage,
  PricingPage,
  ContactPage,
  HelpPage,
  LegalDocumentPage,
  CompanyInfoPage,
} from './routes/lazyAppPages';
import LandingPage from './pages/Public/LandingPage';
import AboutPage from './pages/Public/AboutPage';
import LegalHubPage from './pages/Public/LegalHubPage';
import TrustPage from './pages/Public/TrustPage';

const IS_DEV = process.env.NODE_ENV === 'development';

/** Поддомен документации: сразу каталог /docs, без лендинга markbase.ru */
function PublicMarketingHomeRoute() {
  if (typeof window !== 'undefined' && window.location.hostname === 'docs.markbase.ru') {
    return <Navigate to="/docs" replace />;
  }
  return <LandingPage />;
}

/** markbase.ru/docs → канон docs.markbase.ru (LA-04); на docs.* — HelpPage в SPA */
function MarketingDocsRoute() {
  const location = useLocation();
  if (shouldRedirectMarketingDocsToPortal()) {
    const target = resolveDocsPortalUrl(
      `${location.pathname}${location.search || ''}${location.hash || ''}`
    );
    return <RedirectToUrl url={target} />;
  }
  return <HelpPage />;
}

// Ленивый импорт только вспомогательного shell UI.
const ToastStack = lazy(() => import('./components/Common/ToastStack').then((module) => ({ default: module.ToastStack })));
const ShellHttpOverlays = lazy(() => import('./components/Shell/ShellHttpOverlays'));
const CompanyRequestsPage = lazy(() => import('./pages/Company/CompanyRequestsPage'));
const CookieBanner = lazy(() => import('./components/Legal/CookieBanner'));

// Ошибки lazy-чанков shell (toast/overlays) — retry без F5.
class ChunkErrorBoundary extends React.Component {
  state = { hasError: false, errorCount: 0, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    devLogApiError('React: ChunkErrorBoundary (lazy/chunk)', error);
    if (IS_DEV && errorInfo?.componentStack) {
      // eslint-disable-next-line no-console
      console.warn('[ChunkErrorBoundary] componentStack:', errorInfo.componentStack);
    }

    if (typeof window !== 'undefined' && window.reportError) {
      window.reportError(error);
    }
  }

  handleRetry = () => {
    this.setState(prev => ({
      hasError: false,
      error: null,
      errorCount: prev.errorCount + 1,
    }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <DeployUpdateScreen
          error={this.state.error || undefined}
          reset={this.state.errorCount < 3 ? this.handleRetry : undefined}
        />
      );
    }
    return this.props.children;
  }
}

const withDeferredUi = (node) => <Suspense fallback={null}>{node}</Suspense>;

const RoutePageFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '240px', padding: '40px' }}>
    <Spin size="large" tip="Загрузка..." />
  </div>
);

const withRouteSuspense = (node) => (
  <Suspense fallback={<RoutePageFallback />}>{node}</Suspense>
);

// ===================================================
// REDIRECT TO UAM (auth.markbase.ru)
// Используется для маршрутов, перенесённых в UAM модуль
// ===================================================
const UAM_BASE_URL = UAM_URL;
const RedirectToUAM = ({ path }) => {
  React.useEffect(() => {
    window.location.href = `${UAM_BASE_URL}${path}`;
  }, [path]);
  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      minHeight: '200px', padding: '40px',
    }}>
      <Spin size="large" tip="Перенаправление..." />
    </div>
  );
};

const RedirectToUrl = ({ url }) => {
  React.useEffect(() => {
    window.location.href = url;
  }, [url]);
  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      minHeight: '200px', padding: '40px',
    }}>
      <Spin size="large" tip="Перенаправление..." />
    </div>
  );
};

// Кабинет: с маркетинга/markdocs — всегда app.markbase.ru (не app.docs.markbase.ru)
const markbaseMarketingAppOrigin = () => {
  if (typeof window === 'undefined') return 'https://app.markbase.ru';
  const { protocol, hostname } = window.location;
  return `${protocol}//${resolveMarkbaseProductionAppHostname(hostname)}`;
};

/** Hard-redirect с markbase.ru на app.* до lazy-чанков (иначе ChunkErrorBoundary на /dashboard). */
function MarketingCabinetRedirect() {
  const location = useLocation();
  const isApp = React.useMemo(() => isAppDomain(), []);

  if (isApp) return null;
  if (isPublicMarketingContentPath(location.pathname)) return null;

  const target = resolveMarketingAppRedirectHref(location);
  if (!target || (typeof window !== 'undefined' && target === window.location.href)) return null;

  if (typeof window !== 'undefined') {
    window.location.replace(target);
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh', padding: 40 }}>
      <Spin size="large" tip="Переход в кабинет..." />
    </div>
  );
}

// ===================================================
// LEGACY REDIRECTS (SEO/старые ссылки)
// ===================================================
const LegacyHelpRedirect = () => {
  const location = useLocation();
  const nextPathname = String(location.pathname || '').replace(/^\/help\b/, '/docs');
  return <Navigate to={`${nextPathname}${location.search || ''}${location.hash || ''}`} replace />;
};

// ===================================================
// КОМПОНЕНТ ИНИЦИАЛИЗАЦИИ АВТОРИЗАЦИИ
// ===================================================
const AuthInitializer = ({ children }) => {
  const dispatch = useDispatch();
  // ✅ КРИТИЧЕСКИ ВАЖНО: Мемоизируем результат isAppDomain()
  const isApp = React.useMemo(() => isAppDomain(), []);

  useEffect(() => {
    // ✅ ИСПРАВЛЕНО: Проверяем авторизацию ТОЛЬКО на app поддомене
    // На публичном сайте (markbase.ru) проверка не нужна

    // 🔍 ОТЛАДКА: Логирование для диагностики
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 AuthInitializer:', {
        isApp,
        willCheckAuth: isApp,
        hostname: window.location.hostname
      });
    }

    if (isApp) {
      if (IS_DEV) {
        // eslint-disable-next-line no-console
        console.log('✅ App domain detected - checking authentication');
      }
      // Автоматически проверяем авторизацию при загрузке приложения
      dispatch(checkAuth());
      // После проверки авторизации гарантируем наличие дефолтного поставщика/склада
    } else {
      if (IS_DEV) {
        // eslint-disable-next-line no-console
        console.log('✅ Public domain detected - skipping authentication check');
      }
      // ✅ КРИТИЧЕСКИ ВАЖНО: Убираем флаг загрузки для публичного сайта
      // Без этого ProtectedRoute будет вечно показывать загрузку или делать редирект
      dispatch(skipAuthCheck());
    }
  }, [dispatch, isApp]);

  // Потеря сессии (401 после authReady): очистить Redux и увести на вход с сообщением
  useEffect(() => {
    if (!isApp) return undefined;
    let handled = false;
    const onExpired = () => {
      if (handled) return;
      handled = true;
      try {
        axios.clearGetCache?.();
        axios.clearTokens?.();
      } catch { /* */ }
      dispatch(clearSessionLocal());
      message.warning('Сессия истекла или вход не выполнен. Войдите снова, чтобы продолжить работу.');
      const path = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/dashboard';
      const loginHref = `/login?reason=session_expired&return_url=${encodeURIComponent(path)}`;
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = loginHref;
      }
    };
    window.addEventListener('markbase_session_expired', onExpired);
    return () => window.removeEventListener('markbase_session_expired', onExpired);
  }, [dispatch, isApp]);

  // Мягкая перепроверка сессии: только при возврате на вкладку / bfcache — не каждую секунду
  useEffect(() => {
    if (!isApp) return undefined;
    let lastAt = 0;
    const revalidate = (force = false) => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (!force && now - lastAt < SESSION_REVALIDATE_MIN_MS) return;
      lastAt = now;
      dispatch(checkAuth());
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') revalidate(false);
    };
    const onPageShow = (e) => {
      if (e?.persisted) revalidate(true);
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [dispatch, isApp]);

  return children;
};

// ===================================================
// ОСНОВНОЕ ПРИЛОЖЕНИЕ
// ===================================================
function AppContent() {
  // ✅ КРИТИЧЕСКИ ВАЖНО: Мемоизируем результат isAppDomain()
  // чтобы он не менялся во время рендеринга
  const isApp = React.useMemo(() => isAppDomain(), []);

  // Аналитика после load: не конкурирует с LCP, снижает TBT (Lighthouse)
  React.useEffect(() => {
    const run = async () => {
      try {
        // ✅ ИСПРАВЛЕНО: Используем динамический импорт с правильной обработкой ошибок
        const seoModule = await import('./services/seo.service');
        const seoService = seoModule.default;
        if (seoService && typeof seoService.initAnalytics === 'function') {
          seoService.initAnalytics();
          if (IS_DEV) {
            // eslint-disable-next-line no-console
            console.log('📊 SEO аналитика инициализирована');
          }
        }
      } catch (err) {
        devLogApiError('App: динамический импорт SEO', err);
      }
    };
    if (typeof window !== 'undefined' && document.readyState === 'complete') {
      run();
    } else if (typeof window !== 'undefined') {
      window.addEventListener('load', run, { once: true });
    }
  }, []);

  // 🔍 ОТЛАДКА: Логирование для диагностики роутинга
  if (IS_DEV) {
    // eslint-disable-next-line no-console
    console.log('🔍 AppContent routing:', {
      isApp,
      routeType: isApp ? 'APP (protected)' : 'PUBLIC (no auth)',
      hostname: window.location.hostname,
      pathname: window.location.pathname
    });
  }

  return (
    <ConfigProvider locale={ruRU} theme={antdTheme}>
      <ToastProvider>
        <AuthInitializer>
          <Router>
          <MarketingCabinetRedirect />
          <OnboardingProvider>
          <ChunkErrorBoundary>
            {withDeferredUi(<ShellHttpOverlays />)}
            {withDeferredUi(<CookieBanner />)}
            {withRouteSuspense(
            <Routes>
              {/* ЛОГИКА РАЗДЕЛЕНИЯ ПО ДОМЕНАМ */}
              {!isApp ? (
              /* ===================================================
                 ПУБЛИЧНЫЙ САЙТ (markbase.ru)
                 =================================================== */
              <>
                {/* Публичные страницы (вне Layout) */}
                <Route path="/" element={<PublicMarketingHomeRoute />} />
                <Route path="/product/waygpt" element={<WayGPTPage />} />
                <Route path="/product/mplaza" element={<MplazaPage />} />
                <Route path="/product/mplaza/connection" element={<MplazaConnectionPage />} />
                <Route path="/product/core" element={<CoreSystemPage />} />
                <Route path="/product/constructor" element={<ConstructorPage />} />
                <Route path="/product/crm-hrm" element={<CrmHrmPage />} />
                <Route path="/product/analytics" element={<AnalyticsFinancePage />} />
                <Route path="/product/logistics" element={<LogisticsDocsPage />} />
                <Route path="/product/services" element={<ServicesMarketPage />} />
                <Route path="/product/security" element={<SecurityAdminPage />} />



                <Route path="/about" element={<AboutPage />} />
                <Route path="/legal" element={<LegalHubPage />} />
                <Route path="/trust" element={<TrustPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/company" element={<CompanyInfoPage />} />
                <Route path="/privacy" element={<LegalDocumentPage type="privacy_policy" />} />
                <Route path="/terms" element={<LegalDocumentPage type="terms_of_service" />} />
                <Route path="/personal-data" element={<LegalDocumentPage type="personal_data_processing" />} />
                <Route path="/cookies" element={<LegalDocumentPage type="cookie_consent" />} />

                {/* Документация (новое имя страницы) */}
                <Route path="/docs" element={<HelpPage />} />
                <Route path="/docs/:source/*" element={<HelpPage />} />

                {/* Legacy: /help -> /docs (с сохранением пути) */}
                <Route path="/help" element={<LegacyHelpRedirect />} />
                <Route path="/help/*" element={<LegacyHelpRedirect />} />

                {/* Страницы авторизации - редирект на app поддомен */}
                <Route path="/login" element={<Navigate to={`${markbaseMarketingAppOrigin()}/login`} replace />} />
                <Route path="/register" element={<Navigate to={`${markbaseMarketingAppOrigin()}/register`} replace />} />

                {/* Верификация email - редирект на app поддомен с сохранением токена */}
                <Route path="/verify-email" element={<Navigate to={`${markbaseMarketingAppOrigin()}/verify-email${window.location.search}`} replace />} />

                {/* Редирект /app/* и других страниц приложения на app поддомен */}
                <Route path="/app/*" element={<Navigate to={`${markbaseMarketingAppOrigin()}${window.location.pathname.substring(4)}`} replace />} />
                <Route path="/dashboard" element={<Navigate to={`${markbaseMarketingAppOrigin()}/dashboard`} replace />} />
                <Route path="/products/*" element={<Navigate to={`${markbaseMarketingAppOrigin()}${window.location.pathname}`} replace />} />
                <Route path="/orders/*" element={<Navigate to={`${markbaseMarketingAppOrigin()}${window.location.pathname}`} replace />} />
                <Route path="/warehouses/*" element={<Navigate to={`${markbaseMarketingAppOrigin()}${window.location.pathname}`} replace />} />
                <Route path="/suppliers/*" element={<Navigate to={`${markbaseMarketingAppOrigin()}${window.location.pathname}`} replace />} />
                <Route path="/marketplaces/*" element={<Navigate to={`${markbaseMarketingAppOrigin()}${window.location.pathname}`} replace />} />
                <Route path="/analytics/*" element={<Navigate to={`${markbaseMarketingAppOrigin()}${window.location.pathname}`} replace />} />
                <Route path="/sync/*" element={<Navigate to={`${markbaseMarketingAppOrigin()}${window.location.pathname}`} replace />} />
                <Route path="/connections" element={<Navigate to={`${markbaseMarketingAppOrigin()}/connections`} replace />} />
                <Route path="/connections/*" element={<Navigate to={`${markbaseMarketingAppOrigin()}${window.location.pathname}${window.location.search}`} replace />} />
                <Route path="/api-keys" element={<Navigate to={`${markbaseMarketingAppOrigin()}/api-keys`} replace />} />
                <Route path="/exchange-log" element={<Navigate to={`${markbaseMarketingAppOrigin()}/exchange-log`} replace />} />
                <Route path="/api-integration" element={<Navigate to={`${markbaseMarketingAppOrigin()}/api-integration`} replace />} />
                <Route path="/integrations" element={<Navigate to={`${markbaseMarketingAppOrigin()}/integrations`} replace />} />
                <Route path="/integrations/*" element={<Navigate to={`${markbaseMarketingAppOrigin()}${window.location.pathname}`} replace />} />
                <Route path="/settings/*" element={<Navigate to={`${markbaseMarketingAppOrigin()}${window.location.pathname}`} replace />} />
                <Route path="/users/*" element={<Navigate to={`${markbaseMarketingAppOrigin()}${window.location.pathname}`} replace />} />
                <Route path="/dictionaries/*" element={<Navigate to={`${markbaseMarketingAppOrigin()}${window.location.pathname}`} replace />} />
                <Route path="/admin/*" element={<Navigate to={`${markbaseMarketingAppOrigin()}${window.location.pathname}`} replace />} />
                <Route path="/admin/security" element={<Navigate to={`${markbaseMarketingAppOrigin()}/admin/security`} replace />} />

                {/* 404 для всех остальных маршрутов */}
                <Route path="*" element={<NotFoundPage />} />
              </>
            ) : (
              /* ===================================================
                 ПРИЛОЖЕНИЕ (app.markbase.ru)
                 =================================================== */
              <>
                {/* Страницы авторизации (вне Layout) */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route path="/verify-email" element={<EmailVerification />} />
                {/* Публичные юридические страницы (вне Layout) */}
                <Route path="/company" element={<CompanyInfoPage />} />
                <Route path="/legal" element={<LegalHubPage />} />
                <Route path="/trust" element={<TrustPage />} />
                <Route path="/privacy" element={<LegalDocumentPage type="privacy_policy" />} />
                <Route path="/terms" element={<LegalDocumentPage type="terms_of_service" />} />
                <Route path="/personal-data" element={<LegalDocumentPage type="personal_data_processing" />} />
                <Route path="/cookies" element={<LegalDocumentPage type="cookie_consent" />} />
                <Route path="/401" element={<UnauthorizedPage />} />
                <Route path="/403" element={<ForbiddenPage />} />
                <Route path="/429" element={<TooManyRequestsPage />} />
                <Route path="/500" element={<InternalServerErrorPage />} />
                <Route path="/503" element={<MaintenancePage />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/account-suspended" element={<AccountSuspendedPage />} />
                {/* ✅ Публичная страница для проверки авторизации через iframe (используется с markbase.ru) */}
                <Route path="/auth-check" element={<AuthCheckPage />} />

                {/* Защищенные маршруты (приложение) - БЕЗ префикса /app */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }>
              {/* Главная страница приложения */}
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="menu-guide" element={<MenuGuidePage />} />
              <Route path="dashboard" element={(
                  <ProtectedRoute requireCompany>
                    <Dashboard />
                  </ProtectedRoute>
              )} />
              <Route path="workspace" element={(
                  <ProtectedRoute requireCompany>
                    <CompanyWorkspacePage />
                  </ProtectedRoute>
              )} />
              {/* ВЭЙГПТ и документация ссылались на /dashboard/companies — такого маршрута нет; канон: подключённые компании */}
              <Route path="dashboard/companies" element={<Navigate to="/settings?tab=connected_companies" replace />} />

              {/* Товары - требуется право просмотра товаров */}
              <Route path="products" element={(
                  <ProtectedRoute permission={PERMISSIONS.PRODUCTS_VIEW} requireCompany moduleKey="products">
                    <ProductsPage />
                  </ProtectedRoute>
              )} />
              <Route path="products/new" element={(
                  <ProtectedRoute permission={PERMISSIONS.PRODUCTS_CREATE} requireCompany moduleKey="products">
                    <ProductsPage />
                  </ProtectedRoute>
              )} />
              <Route path="products/:id/edit" element={(
                  <ProtectedRoute permission={PERMISSIONS.PRODUCTS_UPDATE} requireCompany moduleKey="products">
                    <ProductsPage />
                  </ProtectedRoute>
              )} />
              <Route path="products/import-wizard" element={(
                  <ProtectedRoute permission={PERMISSIONS.PRODUCTS_IMPORT} requireCompany>
                    <ProductImportWizard />
                  </ProtectedRoute>
              )} />

              {/* Группы товаров (вариации) - требуется право просмотра групп */}
              <Route path="product-groups" element={(
                  <ProtectedRoute permission={PERMISSIONS.PRODUCT_GROUPS_VIEW} requireCompany moduleKey="products">
                    <ProductGroupsPage />
                  </ProtectedRoute>
              )} />

              {/* Составные товары (BOM) - редирект на единый workspace */}
              <Route path="products/composite" element={<Navigate to="/products?mode=composite" replace />} />

              {/* Маркировка «Честный ЗНАК» - регистрация, настройки */}
              <Route path="marking" element={(
                  <ProtectedRoute requireCompany>
                    <MarkingPage />
                  </ProtectedRoute>
              )} />

              {/* Срок годности и партионный учёт */}
              <Route path="expiry-settings" element={(
                  <ProtectedRoute permission={PERMISSIONS.PRODUCTS_VIEW} requireCompany>
                    <ExpirySettingsPage />
                  </ProtectedRoute>
              )} />

              {/* Legacy platform /orders → единый ORDERS inbox (commerce W2, FAIL-CC-07) */}
              <Route path="orders/:orderId" element={<Navigate to="/modules/orders" replace />} />
              <Route path="orders/*" element={<Navigate to="/modules/orders" replace />} />

              {/* Маркетплейсы - требуется право просмотра маркетплейсов */}
              {/* MARKETPLACE_KONTUR (№55): connection hub before legacy catalog */}
              <Route path="modules/marketplace/connection" element={(
                  <ProtectedRoute permission={PERMISSIONS.MARKETPLACES_VIEW} requireCompany>
                    <MarketplaceKonturConnectionPage />
                  </ProtectedRoute>
              )} />
              {/* Wave 4: legacy /marketplaces → INTEGRATIONS marketplace (MarketplacesPage UI removed) */}
              <Route path="marketplaces/*" element={<Navigate to="/connections?path=/admin/marketplace" replace />} />

              {/* Остатки и синхронизация (отдельная страница, DS) — до catch-all `warehouses/*` */}
              <Route path="warehouses/manager" element={(
                  <ProtectedRoute permission={PERMISSIONS.WAREHOUSES_VIEW} requireCompany moduleKey="warehouses">
                    <WarehouseManager />
                  </ProtectedRoute>
              )} />
              <Route path="warehouses/ulr-operations" element={(
                  <ProtectedRoute permission={PERMISSIONS.WAREHOUSES_VIEW} requireCompany moduleKey="warehouses">
                    <UlrOperationsPage />
                  </ProtectedRoute>
              )} />
              {/* Склады - требуется право просмотра складов */}
              <Route path="warehouses/*" element={(
                  <ProtectedRoute permission={PERMISSIONS.WAREHOUSES_VIEW} requireCompany moduleKey="warehouses">
                    <WarehousesPage />
                  </ProtectedRoute>
              )} />

              {/* Поставщики - требуется право просмотра поставщиков */}
              <Route path="suppliers/*" element={(
                  <ProtectedRoute permission={PERMISSIONS.SUPPLIERS_VIEW} requireCompany moduleKey="suppliers">
                    <SuppliersPage />
                  </ProtectedRoute>
              )} />

              {/* Управление изображениями */}
              <Route path="images-management" element={(
                  <ProtectedRoute permission={PERMISSIONS.PRODUCTS_UPDATE} requireCompany>
                    <ImagesManagementPage />
                  </ProtectedRoute>
              )} />

              {/* Управление импортом файлов */}
              <Route path="file-import" element={(
                  <ProtectedRoute permission={PERMISSIONS.FILE_IMPORT_VIEW} requireCompany moduleKey="integrations">
                    <FileManagementPage />
                  </ProtectedRoute>
              )} />
              {/* Поддержка адреса /file-import/manage как в документации */}
              <Route path="file-import/manage" element={(
                  <ProtectedRoute permission={PERMISSIONS.FILE_IMPORT_VIEW} requireCompany moduleKey="integrations">
                    <FileManagementPage />
                  </ProtectedRoute>
              )} />

              {/* Маппинг полей файла поставщика (query ?supplier=) — импорт файлов + поставщики */}
              <Route path="mapping" element={(
                  <ProtectedRoute permission={PERMISSIONS.FILE_IMPORT_VIEW} requireCompany>
                    <SupplierMapping />
                  </ProtectedRoute>
              )} />

              {/* Автоматизации - требуется право просмотра поставщиков */}
              <Route path="automations" element={(
                  <ProtectedRoute permission={PERMISSIONS.SUPPLIERS_VIEW} requireCompany moduleKey="automations">
                    <AutomationsPage />
                  </ProtectedRoute>
              )} />

              {/* Экспорт и фиды каталога PIM */}
              <Route path="catalog-exports" element={(
                  <ProtectedRoute permission={PERMISSIONS.PRODUCTS_EXPORT} requireCompany>
                    <CatalogExportsPage />
                  </ProtectedRoute>
              )} />
              <Route path="catalog-exports/new" element={(
                  <ProtectedRoute permission={PERMISSIONS.PRODUCTS_EXPORT} requireCompany>
                    <CatalogExportWizard />
                  </ProtectedRoute>
              )} />
              <Route path="catalog-exports/*" element={(
                  <ProtectedRoute permission={PERMISSIONS.PRODUCTS_EXPORT} requireCompany>
                    <CatalogExportEditor />
                  </ProtectedRoute>
              )} />

              {/* Wave 1: /integrations → хаб Подключения; authorize KEEP без редиректа */}
              <Route path="integrations/authorize" element={(
                  <ProtectedRoute requireCompany>
                    <ShopIntegrationAuthorizePage />
                  </ProtectedRoute>
              )} />
              <Route path="integrations" element={<Navigate to="/connections" replace />} />
              <Route path="integrations/*" element={<Navigate to="/connections" replace />} />

              {/* CORE_users (№52): connection guide before company users hub */}
              <Route path="users/connection" element={(
                  <ProtectedRoute permission={PERMISSIONS.USERS_VIEW} requireCompany>
                    <UsersConnectionPage />
                  </ProtectedRoute>
              )} />
              {/* CORE_users (№52): CompanyUsersPage — SSOT markbaseCORE/modules/users */}
              <Route path="users/*" element={(
                  <ProtectedRoute permission={PERMISSIONS.USERS_VIEW} requireCompany>
                    <CompanyUsersPageHost />
                  </ProtectedRoute>
              )} />

              {/* Аналитика - требуется право просмотра аналитики */}
              <Route path="analytics/connection" element={(
                  <ProtectedRoute permission={PERMISSIONS.ANALYTICS_VIEW} requireCompany moduleKey="analytics">
                    <AnalyticsConnectionPage />
                  </ProtectedRoute>
              )} />
              <Route path="analytics/settings" element={(
                  <ProtectedRoute permission={PERMISSIONS.ANALYTICS_ADMIN} requireCompany moduleKey="analytics">
                    <AnalyticsSettingsPage />
                  </ProtectedRoute>
              )} />
              <Route path="analytics" element={(
                  <ProtectedRoute permission={PERMISSIONS.ANALYTICS_VIEW} requireCompany moduleKey="analytics">
                    <AnalyticsPage />
                  </ProtectedRoute>
              )} />
              <Route path="analytics/*" element={(
                  <ProtectedRoute permission={PERMISSIONS.ANALYTICS_VIEW} requireCompany moduleKey="analytics">
                    <AnalyticsPage />
                  </ProtectedRoute>
              )} />

              {/* Wave 7: SyncPage UI deleted; /sync → журнал обменов */}
              <Route path="sync/*" element={<Navigate to="/exchange-log" replace />} />

              {/* Wave 1 канон: Ключи API */}
              <Route path="api-keys" element={(
                  <ProtectedRoute
                    permission={PERMISSIONS.SETTINGS_VIEW}
                    requireCompany
                    requireVerifiedCompany
                    moduleKey="integrations"
                  >
                    <ApiKeysHubPage />
                  </ProtectedRoute>
              )} />
              {/* Wave 3: ApiKeysHub (три полки); старые URL ключей → канон */}
              <Route path="settings/api-keys" element={<Navigate to="/api-keys" replace />} />

              {/* Настройки - требуется право просмотра настроек */}
              <Route path="settings/*" element={(
                  <ProtectedRoute permission={PERMISSIONS.SETTINGS_VIEW}>
                    <SettingsPage />
                  </ProtectedRoute>
              )} />

              {/* Подключенные компании - только для администраторов */}
              <Route path="settings/connected-companies" element={(
                  <ProtectedRoute>
                    <ConnectedCompaniesPage />
                  </ProtectedRoute>
              )} />

              {/* Настройки цен - требуется право просмотра настроек */}
              <Route path="settings/prices" element={(
                  <ProtectedRoute permission={PERMISSIONS.SETTINGS_VIEW}>
                    <PriceHubPage />
                  </ProtectedRoute>
              )} />

              {/* Настройки валют - требуется право просмотра настроек */}
              <Route path="settings/currencies" element={(
                  <ProtectedRoute permission={PERMISSIONS.SETTINGS_VIEW}>
                    <CurrencySettingsPage />
                  </ProtectedRoute>
              )} />

              {/* WAYGPT_KONTUR (№6): Markbase proxy hub */}
              <Route path="modules/waygpt-kontur/connection" element={(
                  <ProtectedRoute requireCompany>
                    <WaygptKonturConnectionPage />
                  </ProtectedRoute>
              )} />
              {/* AISERVER_WAYGPT (№56): runtime hub + boundary WAYGPT_KONTUR (№6) */}
              <Route path="modules/waygpt/connection" element={(
                  <ProtectedRoute requireCompany>
                    <AiserverWaygptConnectionPage />
                  </ProtectedRoute>
              )} />
              <Route path="settings/waygpt-control" element={(
                  <ProtectedRoute
                    permissions={[PERMISSIONS.SETTINGS_VIEW, PERMISSIONS.ADMIN_ACCESS]}
                    requireAll={false}
                    requireCompany
                  >
                    <AiWaygptSettingsHubPage />
                  </ProtectedRoute>
              )} />

              {/* AI Platform Master W1a: sidebar «Нейроуправление» entry points */}
              <Route path="neuro" element={<Navigate to="/neuro/operator" replace />} />
              <Route path="neuro/history" element={(
                  <ProtectedRoute requireCompany>
                    <NeuroActionHistoryPage />
                  </ProtectedRoute>
              )} />
              <Route path="neuro/:slug" element={(
                  <ProtectedRoute requireCompany>
                    <NeuroCompanionOpenPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/waygpt-control" element={<Navigate to="/settings/waygpt-control" replace />} />
              <Route path="admin/assistant" element={<Navigate to="/settings/waygpt-control?tab=assistant" replace />} />

              {/* Создание компании — доступно без активной компании */}
              <Route path="company/create" element={(
                  <ProtectedRoute>
                    <CompanyCreationWizard />
                  </ProtectedRoute>
              )} />

              {/* Присоединение к компании по invite_code */}
              <Route path="company/join" element={(
                  <ProtectedRoute>
                    <JoinCompanyPage />
                  </ProtectedRoute>
              )} />

              <Route path="company/requests" element={(
                  <ProtectedRoute>
                    <CompanyRequestsPage />
                  </ProtectedRoute>
              )} />

              {/* Наша компания - требуется право просмотра настроек */}
              <Route path="our-company" element={(
                  <ProtectedRoute permission={PERMISSIONS.SETTINGS_VIEW} requireCompany>
                    <OurCompanyPage />
                  </ProtectedRoute>
              )} />

              {/* Анализ компании - требуется право просмотра настроек */}
              <Route path="company-analysis/:inn" element={(
                  <ProtectedRoute permission={PERMISSIONS.SETTINGS_VIEW}>
                    <CompanyAnalysisPage />
                  </ProtectedRoute>
              )} />

              {/* ✅ ПЕРЕНЕСЕНО В МОДУЛИ (UAM — auth.markbase.ru):
                  - BalancePage → auth.markbase.ru/account/balance
                  - TariffsPage → auth.markbase.ru/account/tariffs
                  Редирект на UAM для обратной совместимости */}
              <Route path="balance" element={(
                <ProtectedRoute requireCompany>
                  <RedirectToUrl url={WALLET_PAGE_URL} />
                </ProtectedRoute>
              )} />
              <Route path="tariffs" element={<RedirectToUrl url={BILLING_PAGE_URL} />} />

              {/* Детализация расходов */}
              <Route path="cost-details" element={(
                  <ProtectedRoute requireCompany moduleKey="finance">
                    <CostDetailsPage />
                  </ProtectedRoute>
              )} />

              <Route path="finance-overview" element={(
                  <ProtectedRoute requireCompany moduleKey="finance">
                    <FinanceOverviewPage />
                  </ProtectedRoute>
              )} />
              <Route path="finance" element={<Navigate to="/finance-overview" replace />} />

              {/* Wave 1: /api-integration → /api-keys */}
              <Route path="api-integration" element={<Navigate to="/api-keys" replace />} />

              {/* Wave 1 канон: Подключения = embed INTEGRATIONS /admin */}
              <Route path="connections" element={(
                  <ProtectedRoute
                    permissions={[
                      PERMISSIONS.MARKETPLACES_VIEW,
                      PERMISSIONS.SETTINGS_VIEW,
                      PERMISSIONS.SYNC_VIEW_LOGS,
                    ]}
                    requireAll={false}
                    requireCompany
                    moduleKey="integrations"
                  >
                    <IntegrationsAdminModulePage moduleName="Подключения" />
                  </ProtectedRoute>
              )} />

              {/* Wave 1 канон: Журнал обменов = embed /admin/journal */}
              <Route path="exchange-log" element={(
                  <ProtectedRoute
                    permissions={[PERMISSIONS.SYNC_VIEW_LOGS, PERMISSIONS.SYNC_EXECUTE, PERMISSIONS.MARKETPLACES_VIEW]}
                    requireAll={false}
                    requireCompany
                    moduleKey="integrations"
                  >
                    <IntegrationsAdminModulePage
                      embedPath="/admin/journal"
                      moduleName="Журнал обменов"
                    />
                  </ProtectedRoute>
              )} />

              {/* Уведомления - доступны всем авторизованным пользователям */}
              <Route path="notifications/connection" element={(
                  <ProtectedRoute>
                    <NotificationsConnectionPage />
                  </ProtectedRoute>
              )} />
              <Route path="notifications" element={(
                  <ProtectedRoute>
                    <NotificationsPage />
                  </ProtectedRoute>
              )} />

              {/* Справочники - управление категориями, брендами, атрибутами */}
              {/* Доступны всем авторизованным пользователям для просмотра и работы с данными */}
              <Route path="dictionaries/*" element={(
                  <ProtectedRoute requireCompany moduleKey="dictionaries">
                    <DictionariesPage />
                  </ProtectedRoute>
              )} />

              {/* Скидки по брендам (dockprice) */}
              <Route path="dictionaries/brand-discounts" element={(
                  <ProtectedRoute permission={PERMISSIONS.SUPPLIERS_UPDATE} requireCompany moduleKey="dictionaries">
                    <BrandDiscountsPage />
                  </ProtectedRoute>
              )} />

              {/* Удалено: страница интеграций - функциональность перенесена в поставщики */}
              {/* Harvest страница отключена: все скачивания выполняются на странице Товары */}
              {/* Админские страницы - только для администраторов */}
              {/* Monitoring moved to markbaseCORE → monitoring.markbase.ru */}

              {/* /admin/files удалён: модуль FILES — единственный SSOT файлов; */}
              {/* legacy локальный файловый браузер (fs.promises над cwd) убран по решению 2026-04. */}

              <Route path="admin" element={<Navigate to="/admin/companies" replace />} />

              <Route path="admin/platform-meta/connection" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <PlatformMetaConnectionPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/landing/connection" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <LandingConnectionPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/seo" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <SEOPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/companies" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <CompaniesPage />
                  </ProtectedRoute>
              )} />

              {/* ✅ ПЕРЕНЕСЕНО В МОДУЛЬ (UAM — auth.markbase.ru):
                  BillingAdminPage → auth.markbase.ru/account/admin/tariffs
                  Редирект на UAM для обратной совместимости */}
              <Route path="admin/billing" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <RedirectToUrl url={BILLING_ADMIN_PAGE_URL} />
                  </ProtectedRoute>
              )} />

              {/* Локальная SuperAdmin страница биллинга: тарифы markbase.ru, компании, реестр модулей */}
              <Route path="admin/billing-management" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <BillingManagementPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/registry" element={(
                <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                  <RegistryAdminHubPage />
                </ProtectedRoute>
              )} />

              <Route path="admin/plugin-hub/connection" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <PluginHubConnectionPage />
                  </ProtectedRoute>
              )} />
              <Route path="admin/plugin-hub" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <PluginHubAdminPage />
                  </ProtectedRoute>
              )} />

              {/* Module 10b — Notifications settings (SMTP, Telegram, channels, dry-run, stats) */}
              <Route path="admin/notifications" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <NotificationsAdminPage />
                  </ProtectedRoute>
              )} />

              {/* Module 12a — Kassa */}
              <Route path="kassa"               element={<ProtectedRoute requireCompany permission={PERMISSIONS.KASSA_VIEW}><KassaPOSPage /></ProtectedRoute>} />
              <Route path="kassa/pos"           element={<ProtectedRoute requireCompany permission={PERMISSIONS.KASSA_VIEW}><KassaPOSPage /></ProtectedRoute>} />
              <Route path="kassa/admin"         element={<ProtectedRoute requireCompany permission={PERMISSIONS.KASSA_MANAGE}><KassaAdminPage /></ProtectedRoute>} />
              <Route path="kassa/admin/connection" element={<ProtectedRoute requireCompany permission={PERMISSIONS.KASSA_MANAGE}><KassaConnectionPage /></ProtectedRoute>} />
              <Route path="kassa/reports"       element={<ProtectedRoute requireCompany permissions={[PERMISSIONS.KASSA_REPORTS, PERMISSIONS.KASSA_VIEW]} requireAll={false}><KassaReportsPage /></ProtectedRoute>} />
              <Route path="kassa/self-checkout" element={<ProtectedRoute requireCompany permission={PERMISSIONS.KASSA_SELL}><SelfCheckoutPage /></ProtectedRoute>} />

              {/* Module 12 — Documents */}
              <Route path="documents"                element={<ProtectedRoute permission={PERMISSIONS.DOCUMENTS_VIEW} requireCompany><DocumentsPage /></ProtectedRoute>} />
              <Route path="documents/new"            element={<ProtectedRoute permission={PERMISSIONS.DOCUMENTS_VIEW} requireCompany><DocumentsPage /></ProtectedRoute>} />
              <Route path="documents/:id"            element={<ProtectedRoute permission={PERMISSIONS.DOCUMENTS_VIEW} requireCompany><DocumentDetailPage /></ProtectedRoute>} />
              <Route path="documents/incoming"       element={<ProtectedRoute permission={PERMISSIONS.DOCUMENTS_VIEW} requireCompany><DocumentsIncomingPage /></ProtectedRoute>} />
              <Route path="documents/settings"       element={<ProtectedRoute permission={PERMISSIONS.DOCUMENTS_MANAGE} requireCompany><DocumentsSettingsPage /></ProtectedRoute>} />
              <Route path="documents/settings/connection" element={<ProtectedRoute permission={PERMISSIONS.DOCUMENTS_MANAGE} requireCompany><DocumentsConnectionPage /></ProtectedRoute>} />
              <Route path="documents/tasks"          element={<ProtectedRoute permission={PERMISSIONS.DOCUMENTS_VIEW} requireCompany><DocumentsPage /></ProtectedRoute>} />
              <Route path="documents/stock-pieces"   element={<ProtectedRoute permission={PERMISSIONS.DOCUMENTS_MANAGE} requireCompany><StockPiecesPage /></ProtectedRoute>} />

              {/* Cabinet — Личный кабинет клиента */}
              <Route path="cabinet/loyalty"         element={<LoyaltyCardPage />} />
              <Route path="cabinet/loyalty/history" element={<LoyaltyCardPage tab="history" />} />
              <Route path="cabinet/poa"             element={<ElectronicPOAPage />} />
              <Route path="cabinet/poa/:companyId"  element={<ElectronicPOAPage />} />
              <Route path="compliance/connection"   element={<ProtectedRoute requireCompany><ComplianceConnectionPage /></ProtectedRoute>} />
              <Route path="compliance"              element={<ProtectedRoute requireCompany><CompliancePage /></ProtectedRoute>} />

              <Route path="admin/pricing-templates" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <PricingTemplatesAdminPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/pricing-rules" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <PricingRulesAdminPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/requisites" element={(
                <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                  <RedirectToUAM path="/account/admin/requisites" />
                </ProtectedRoute>
              )} />

              <Route path="admin/legal-documents" element={(
                <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                  <RedirectToUAM path="/account/admin/legal-documents" />
                </ProtectedRoute>
              )} />

              <Route path="admin/resource-styles" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <ResourceStylesPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/cron-scheduler" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <RedirectToUrl url={SCHEDULER_DASHBOARD_URL} />
                  </ProtectedRoute>
              )} />

              <Route path="admin/backups/connection" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <BackupConnectionPage />
                  </ProtectedRoute>
              )} />

              {/* Soft landing: bare RedirectToUrl left any session on backup.markbase.ru raw JSON
                  403 (backup.read) — hang/dead-end without App chrome. Dashboard opens from connection. */}
              <Route path="admin/backups" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <Navigate to="/admin/backups/connection" replace />
                  </ProtectedRoute>
              )} />

              <Route path="admin/database" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <DatabaseConsolePage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/backup-sources" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <Navigate to="/admin/backups/connection" replace />
                  </ProtectedRoute>
              )} />

              <Route path="admin/docker-agent/connection" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <DockerAgentConnectionPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/logcenter/connection" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <LogCenterConnectionPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/logcenter" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <LogCenterAdminPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/concurens/settings" element={(
                  <ProtectedRoute requireCompany>
                    <ConcurensSettingsAdminPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/companion" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <CompanionAdminPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/concurens/map" element={(
                  <ProtectedRoute requireCompany>
                    <ConcurensMapAdminPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/process-center/connection" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <ProcessCenterConnectionPage />
                  </ProtectedRoute>
              )} />
              <Route path="admin/process-center" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <ProcessCenterAdminPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/monitoring/connection" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <MonitoringConnectionPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/control-plane/connection" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <ControlPlaneConnectionPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/control-plane" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <ControlPlaneAdminPage />
                  </ProtectedRoute>
              )} />

              {/* Legacy email-CRM (/crm/*) удалён: единый CRM — модуль /modules/crm (crm.markbase.ru). */}

              <Route path="admin/api-rate-limits" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <ApiRateLimitsPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/container-resources" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <ContainerResourcesAdminPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/background-jobs" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <BackgroundJobsAdminPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/loyalty-settings" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <LoyaltySettingsAdminPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/navigation-nav" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <NavigationNavAdminPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/security" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <SecurityDashboardPage />
                  </ProtectedRoute>
              )} />

              {/* Встроенные модули экосистемы */}
              <Route path="modules/orders" element={(
                  <ProtectedRoute requireCompany moduleKey="orders">
                    <OrdersModulePage />
                  </ProtectedRoute>
              )} />
              <Route path="modules/delivery" element={(
                  <ProtectedRoute requireCompany moduleKey="delivery">
                    <DeliveryModulePage />
                  </ProtectedRoute>
              )} />
              <Route path="modules/logistics" element={(
                  <ProtectedRoute requireCompany moduleKey="logistics">
                    <LogisticsModulePage />
                  </ProtectedRoute>
              )} />
              <Route path="modules/integrations-admin" element={(
                  <ProtectedRoute
                    permissions={[
                      PERMISSIONS.MARKETPLACES_VIEW,
                      PERMISSIONS.SETTINGS_VIEW,
                      PERMISSIONS.SYNC_VIEW_LOGS,
                    ]}
                    requireAll={false}
                    requireCompany
                    moduleKey="integrations"
                  >
                    <IntegrationsAdminModulePage />
                  </ProtectedRoute>
              )} />
              <Route path="modules/crm" element={(
                  <ProtectedRoute requireCompany moduleKey="crm">
                    <CrmModulePage />
                  </ProtectedRoute>
              )} />
              <Route path="modules/hrm" element={(
                  <ProtectedRoute requireCompany moduleKey="hrm">
                    <HrmModulePage />
                  </ProtectedRoute>
              )} />
              <Route path="modules/files/connection" element={(
                  <ProtectedRoute requireCompany moduleKey="files">
                    <FilesConnectionPage />
                  </ProtectedRoute>
              )} />
              <Route path="modules/files" element={(
                  <ProtectedRoute requireCompany moduleKey="files">
                    <FilesModulePage />
                  </ProtectedRoute>
              )} />
              <Route path="modules/kassa" element={(
                  <ProtectedRoute requireCompany moduleKey="kassa">
                    <KassaModulePage />
                  </ProtectedRoute>
              )} />
              <Route path="modules/search/connection" element={(
                  <ProtectedRoute requireCompany moduleKey="search">
                    <SearchConnectionPage />
                  </ProtectedRoute>
              )} />
              <Route path="modules/search" element={(
                  <ProtectedRoute requireCompany moduleKey="search">
                    <SearchModulePage />
                  </ProtectedRoute>
              )} />
              <Route path="modules/bonus/connection" element={(
                  <ProtectedRoute requireCompany moduleKey="bonus">
                    <BonusConnectionPage />
                  </ProtectedRoute>
              )} />
              <Route path="modules/bonus" element={(
                  <ProtectedRoute requireCompany moduleKey="bonus">
                    <BonusModulePage />
                  </ProtectedRoute>
              )} />
              <Route path="modules/shop" element={(
                  <ProtectedRoute requireCompany moduleKey="shop">
                    <ShopModulePage />
                  </ProtectedRoute>
              )} />

              {/* POWER — производство (work-centers, BOM, OEE, planning, maintenance).
                  Standalone Vite/TS SPA: modouls/POWER/frontend; nginx vhost
                  power.markbase.ru; backend /api/power/v1. Embedded в App через
                  EmbeddedModule. См. cache/verify/POWER.md (Wave 2 / POWER). */}
              <Route path="modules/power/connection" element={(
                  <ProtectedRoute requireCompany moduleKey="power">
                    <PowerConnectionPage />
                  </ProtectedRoute>
              )} />
              <Route path="modules/power" element={(
                  <ProtectedRoute requireCompany moduleKey="power">
                    <PowerModulePage />
                  </ProtectedRoute>
              )} />

              {/* FEED (Молvinka) — инструкция подключения (modoul №39) */}
              <Route path="modules/feed/connection" element={(
                  <ProtectedRoute requireCompany moduleKey="feed">
                    <FeedConnectionPage />
                  </ProtectedRoute>
              )} />

              {/* Лента (Feed) — социальная сеть производителей */}
              <Route path="modules/feed" element={(
                  <ProtectedRoute requireCompany moduleKey="feed">
                    <FeedModulePage />
                  </ProtectedRoute>
              )} />

              {/* MPLAZA (Delgram) — инструкция подключения B2C/B2B маркетплейса */}
              <Route path="product/mplaza/connection" element={(
                  <ProtectedRoute requireCompany>
                    <MplazaAppConnectionPage />
                  </ProtectedRoute>
              )} />

              {/* TORG — тендеры, площадки, мои предложения (нативная App-страница, бэкенд modouls/TORG :8109) */}
              <Route path="modules/torg/connection" element={(
                  <ProtectedRoute requireCompany moduleKey="torg">
                    <TorgConnectionPage />
                  </ProtectedRoute>
              )} />
              <Route path="modules/torg" element={(
                  <ProtectedRoute requireCompany moduleKey="torg">
                    <TorgPage />
                  </ProtectedRoute>
              )} />

              {/* FINANCE — инструкция подключения (modoul №14) */}
              <Route path="modules/finance/connection" element={(
                  <ProtectedRoute requireCompany moduleKey="finance">
                    <FinanceConnectionPage />
                  </ProtectedRoute>
              )} />
              <Route path="modules/pay/connection" element={(
                  <ProtectedRoute requireCompany>
                    <PayKonturConnectionPage />
                  </ProtectedRoute>
              )} />

              {/* P9c App-native Operational Center — attention board + module deep-links */}
              <Route path="ops" element={(
                  <ProtectedRoute requireCompany>
                    <OpsCenterPage />
                  </ProtectedRoute>
              )} />

              {/* W9 Commerce Setup Hub — 8-шаговый навигатор настройки commerce (§Q.5) */}
              <Route path="commerce-hub" element={(
                  <ProtectedRoute requireCompany>
                    <CommerceHubPage />
                  </ProtectedRoute>
              )} />

              {/* FINANCE — KPI-дашборд (P&L, cashflow, долги, налоги, баланс).
                  Нативная App-страница, бэкенд modouls/FINANCE :8086 через
                  nginx app.markbase.conf → /api/finance/v1/*. companyId — server-derived.
                  Wildcard: карточки разделов ведут на /modules/finance/expenses и т.д. */}
              <Route path="modules/finance/*" element={(
                  <ProtectedRoute requireCompany moduleKey="finance">
                    <FinanceModulePage />
                  </ProtectedRoute>
              )} />

              {/* WIDGET_SYSTEM — инструкция подключения (modoul №40) */}
              <Route path="modules/widgets/connection" element={(
                  <ProtectedRoute requireCompany moduleKey="widgets">
                    <WidgetSystemConnectionPage />
                  </ProtectedRoute>
              )} />

              {/* WIDGET_SYSTEM — no-code конструктор виджетов (калькуляторы, квизы,
                  конфигураторы, формы, букинг, календарь). Бэкенд modouls/WIDGET_SYSTEM
                  :8112 через nginx app.markbase.conf → /api/widgets/v1/*. companyId — server-derived.
                  Wildcard: /modules/widgets/:id — билдер, /modules/widgets/leads — заявки. */}
              <Route path="modules/widgets/*" element={(
                  <ProtectedRoute requireCompany moduleKey="widgets">
                    <WidgetSystemModulePage />
                  </ProtectedRoute>
              )} />

              {/* SKLAD WMS — операционный shell склада (планы, задачи, ячейки, инвентаризация, списания).
                  EmbeddedModule на самостоятельный Vite-фронт modouls/SKLAD/frontend через nginx-домен sklad.markbase.ru.
                  Доступ — под requireCompany + permission WAREHOUSES_VIEW (как и /warehouses CRUD). */}
              <Route path="modules/wms" element={(
                  <ProtectedRoute permission={PERMISSIONS.WAREHOUSES_VIEW} requireCompany moduleKey="sklad">
                    <SkladWmsModulePage />
                  </ProtectedRoute>
              )} />

              {/* LEGAL — юридический контур (embedded legal.markbase.ru) */}
              <Route path="modules/legal/connection" element={(
                  <ProtectedRoute requireCompany moduleKey="legal">
                    <LegalConnectionPage />
                  </ProtectedRoute>
              )} />
              <Route path="modules/legal" element={(
                  <ProtectedRoute requireCompany moduleKey="legal">
                    <LegalModulePage />
                  </ProtectedRoute>
              )} />

              {/* SERVICES — маркетплейс услуг (обзорный кабинет App) */}
              <Route path="modules/services" element={(
                  <ProtectedRoute requireCompany moduleKey="services">
                    <ServicesModulePage />
                  </ProtectedRoute>
              )} />

              <Route path="modules/company-services-booking" element={(
                  <ProtectedRoute requireCompany moduleKey="shop">
                    <CompanyServicesBookingHubPage />
                  </ProtectedRoute>
              )} />

              {/* SUPPORT — чат и (для операторов) список сессий */}
              <Route path="modules/support/connection" element={(
                  <ProtectedRoute requireCompany moduleKey="support">
                    <SupportConnectionPage />
                  </ProtectedRoute>
              )} />
              <Route path="modules/support" element={(
                  <ProtectedRoute requireCompany moduleKey="support">
                    <SupportModulePage />
                  </ProtectedRoute>
              )} />

              {/* ВЭЙГЕН — AI контент (нативная страница, API /api/waygen через nginx) */}
              <Route path="waygen/connection" element={(
                  <ProtectedRoute requireCompany moduleKey="waygen">
                    <WaygenConnectionPage />
                  </ProtectedRoute>
              )} />
              <Route path="waygen" element={(
                  <ProtectedRoute requireCompany moduleKey="waygen">
                    <WaygenPage />
                  </ProtectedRoute>
              )} />

              <Route path="modules/notifications/connection" element={(
                  <ProtectedRoute requireCompany moduleKey="notifications">
                    <NotificationsConnectionPage />
                  </ProtectedRoute>
              )} />
              {/* Alias: уведомления (пункт сайдбара «модуль» → тот же Inbox) */}
              <Route path="modules/notifications" element={(
                  <ProtectedRoute requireCompany moduleKey="notifications">
                    <NotificationsPage />
                  </ProtectedRoute>
              )} />

              {/* Страница чата - полный экран (AI-ассистент) */}
              <Route path="chat" element={(
                  <ProtectedRoute requireCompany>
                    <ChatPage />
                  </ProtectedRoute>
              )} />

              {/* Страница чатов — omnichannel (modouls/CHATS) */}
              <Route path="chats/connection" element={(
                  <ProtectedRoute requireCompany>
                    <ChatsConnectionPage />
                  </ProtectedRoute>
              )} />
              <Route path="chats/:conversationId?" element={(
                  <ProtectedRoute requireCompany>
                    <ChatsPage />
                  </ProtectedRoute>
              )} />

              <Route path="admin/search-settings" element={(
                  <ProtectedRoute permission={PERMISSIONS.ADMIN_ACCESS}>
                    <Navigate to="/modules/search" replace />
                  </ProtectedRoute>
              )} />

              {/* Style System 2026 Showcase */}
              <Route path="style-system-2026" element={(
                  <ProtectedRoute>
                    <StyleShowcasePage />
                  </ProtectedRoute>
              )} />
            </Route>

                {/*
                  Публичные страницы на app.*: рендер локально.
                  Нельзя <Navigate to="https://markbase.ru/..."> — History.replaceState
                  между origins бросает SecurityError и валит ChunkErrorBoundary.
                  SoftCapacity / CTA → /pricing остаются на app без hard-crash.
                */}
                <Route path="about" element={<AboutPage />} />
                <Route path="pricing" element={<PricingPage />} />
                <Route path="contact" element={<ContactPage />} />

                {/* 404 для всех остальных маршрутов (вне Layout) */}
                <Route path="*" element={<NotFoundPage />} />
              </>
            )}
              </Routes>
            )}
            </ChunkErrorBoundary>
          </OnboardingProvider>
          </Router>
        </AuthInitializer>
        {withDeferredUi(<ToastStack />)}
      </ToastProvider>
    </ConfigProvider>
  );
}

function App() {
  return (
    <Provider store={store}>
      <BillingProvider axios={axios}>
        <TariffProvider axios={axios}>
            <AppContent />
        </TariffProvider>
      </BillingProvider>
    </Provider>
  );
}

export default App;
